'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/lib/stores/auth-store'
import { ActivityIndicator } from '@/components/ui/activity-indicator'
import { handleAuthError } from '@/lib/api-error-handler'

interface AuthGuardProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

export function AuthGuard({ children, fallback }: AuthGuardProps): React.ReactElement {
  const { isAuthenticated, isLoading, isInitialized, initializeAuth, hydrateProfile, session, user } = useAuth()
  const router = useRouter()

  useEffect(() => {
    if (!isInitialized) {
      initializeAuth()
    }
  }, [isInitialized, initializeAuth, session, user, isAuthenticated])

  // Once authenticated, refresh the user from the canonical org profile so the
  // real first/last name appears even if the session token lacked metadata.
  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      hydrateProfile()
    }
  }, [isInitialized, isAuthenticated, hydrateProfile])

  useEffect(() => {
    if (isInitialized && !isLoading && !isAuthenticated) {
      handleAuthError({ message: 'Not authenticated' })
    }
  }, [isAuthenticated, isLoading, isInitialized, router])

  if (!isInitialized || isLoading) {
    return (
      (fallback as React.ReactElement) || <div className="fixed inset-0 flex items-center justify-center bg-background"><ActivityIndicator size={64} /></div>
    )
  }

  if (!isAuthenticated) {
    return <div className="fixed inset-0 flex items-center justify-center bg-background"><ActivityIndicator size={64} /></div>
  }

  return <>{children}</>
}

export function useAuthGuard() {
  const { isAuthenticated, isLoading, isInitialized, user, session } = useAuth()

  return {
    isAuthenticated,
    isLoading,
    isInitialized,
    user,
    session,
    canRender: isInitialized && !isLoading && isAuthenticated,
  }
}
